import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Building2, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function Locations() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => { const r = await api.get('/locations'); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="locations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">{lang === 'es' ? 'Locales / Sucursales' : 'Locais / Filiais'}</h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Gestiona múltiples unidades de tu restaurante.' : 'Gerencie múltiplas unidades do seu restaurante.'}</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="locations-new-button">
          <Plus className="h-4 w-4 mr-1" /> {lang === 'es' ? 'Nuevo local' : 'Novo local'}
        </Button>
      </div>
      {items.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Building2 className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Sin locales' : 'Sem locais'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(loc => (
            <Card key={loc.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setEditing(loc); setOpen(true); }} data-testid={`location-card-${loc.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {loc.color && <span className="h-3 w-3 rounded-full shrink-0" style={{ background: loc.color }} />}
                    <h3 className="font-heading font-semibold truncate">{loc.nombre}</h3>
                  </div>
                  {loc.es_principal && <Badge variant="default" className="text-[10px] gap-1"><Star className="h-3 w-3" /> Principal</Badge>}
                </div>
                {loc.direccion && <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{loc.direccion}</div>}
                {loc.telefono && <div className="text-xs text-[hsl(var(--muted-foreground))]">{loc.telefono}</div>}
                {!loc.activa && <Badge variant="outline" className="mt-2 text-[10px]">Inactivo</Badge>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <LocationDialog open={open} onOpenChange={setOpen} editing={editing} t={t} lang={lang} onSaved={() => { setOpen(false); load(); window.dispatchEvent(new Event('locations-changed')); }} />
    </div>
  );
}

function LocationDialog({ open, onOpenChange, editing, t, lang, onSaved }) {
  const [form, setForm] = useState({
    nombre: '', direccion: '', telefono: '', email: '', nif: '',
    color: '#E07856', activa: true, es_principal: false,
  });
  useEffect(() => {
    if (editing) setForm(editing);
    else setForm({ nombre: '', direccion: '', telefono: '', email: '', nif: '', color: '#E07856', activa: true, es_principal: false });
  }, [editing, open]);
  const save = async () => {
    if (editing) await api.put(`/locations/${editing.id}`, form);
    else await api.post('/locations', form);
    toast.success('OK'); onSaved();
  };
  const remove = async () => {
    if (!confirm('Eliminar local?')) return;
    try {
      await api.delete(`/locations/${editing.id}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="location-dialog">
        <DialogHeader><DialogTitle>{editing ? t('edit') : (lang === 'es' ? 'Nuevo local' : 'Novo local')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="location-name-input" placeholder={lang === 'es' ? 'Cantinho — Gótico' : 'Cantinho — Gótico'} /></div>
          <div><Label>{t('address')}</Label><Input value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('phone')}</Label><Input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} /></div>
            <div><Label>{t('nif')}</Label><Input value={form.nif} onChange={e => setForm({ ...form, nif: e.target.value })} /></div>
          </div>
          <div><Label>{t('email')}</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div><Label>{lang === 'es' ? 'Color identificador' : 'Cor identificadora'}</Label><Input type="color" value={form.color || '#E07856'} onChange={e => setForm({ ...form, color: e.target.value })} className="h-10" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label className="text-xs">{lang === 'es' ? 'Principal' : 'Principal'}</Label><Switch checked={form.es_principal} onCheckedChange={v => setForm({ ...form, es_principal: v })} /></div>
              <div className="flex items-center justify-between"><Label className="text-xs">{lang === 'es' ? 'Activa' : 'Ativa'}</Label><Switch checked={form.activa} onCheckedChange={v => setForm({ ...form, activa: v })} /></div>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="destructive" onClick={remove} data-testid="location-delete-button"><Trash2 className="h-4 w-4" /></Button>}
          <Button onClick={save} disabled={!form.nombre} data-testid="location-save-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
